import { IonIcon } from '@ionic/react'
import { SolarPower } from '@mui/icons-material'
import { Gauge } from 'gauge-chart-js'
import { flash, leaf, logoEuro } from 'ionicons/icons'
import React, { useContext, useEffect, useRef } from 'react'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import { logEvent } from '../../../../analytics/utils/firebaseUtils'
import { AppContext } from '../../../../contexts/AppContext'
import { ConsumptionData } from '../../../../contexts/ConsumptionDataContext'
import useUser from '../../../../contexts/hooks/useUser'
import { SolarPanelHook } from '../../../devices/solarPanel/hooks/useSolarPanel'
import { MainPagePanelView } from '../MainView'
import ConsumptionCircle from './ConsumptionCircle'
import MoneyCircle from './MoneyCircle'
import NatureCircle from './NatureCircle'
import ProductionCircle from './ProductionCircle'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const gradstop = require('gradstop')

interface Props {
	history: any
	consideredMonth: ConsumptionData
	comparisonMonth: ConsumptionData
	inverter: SolarPanelHook | undefined
	activeElement: MainPagePanelView
	setActiveElement: React.Dispatch<React.SetStateAction<MainPagePanelView>>
}

const CircleCarousel: React.FC<Props> = ({
	history,
	consideredMonth,
	comparisonMonth,
	inverter,
	activeElement,
	setActiveElement,
}) => {
	const { isDarkMode } = useContext(AppContext)

	const { user } = useUser()

	const sliderRef = useRef<any>()
	const caugeElement = useRef<any>()

	const handleChange: any = (next: number) => {
		switch (next) {
			case 0:
				return setActiveElement('money')
			case 1:
				return setActiveElement('consumption')
			case 2:
				return setActiveElement('nature')
			case 3:
				if (!user?.panelLinked) return handleChange(0)
				return setActiveElement('production')
		}
	}

	const createGauge = (percentage: number) => {
		if (!caugeElement.current) return

		const value = (360 / 100) * percentage

		const sharedConfig = {
			container: caugeElement.current,
			lineWidth: 1.5,
			value: 0,
			fromAngle: 0,
			toAngle: 360,
		}

		new Gauge({
			...sharedConfig,
			color: isDarkMode ? '#063a3e' : '#f5f5f5',
		}).setValue(360)

		new Gauge({
			...sharedConfig,
			animationDuration: 3000,
			animationDelay: 200,
			colors: gradstop({
				stops: 360,
				colorArray: ['#3cb49f', '#2dc423', '#cbf205', '#f41938'],
			}),
		}).setValue(value)
	}

	useEffect(() => {
		if (consideredMonth.kwh < 1 || comparisonMonth.kwh < 1) return

		if (user?.panelLinked && activeElement === 'production') createGauge(100)
		createGauge((consideredMonth.kwh / comparisonMonth.kwh) * 100)
	}, [consideredMonth, comparisonMonth, activeElement, isDarkMode])

	const settings = {
		initialSlide: 0,
		fade: true,
		arrows: false,
		infinite: true,
		speed: 500,
		slidesToShow: 1,
		slidesToScroll: 1,
		beforeChange: (_: number, next: number) => {
			handleChange(next)
		},
	}

	return (
		<div id='circle-carousel' className='z-10 relative w-full'>
			<div className='hidden lg:block' />
			<div
				onClick={() => {
					logEvent('click-circle-carousel-previous')
					sliderRef.current.slickPrev()
				}}
				className='small-circle button-hover text-text-grey-200 dark:text-background-200-light bg-white dark:bg-background-200-dark border-solid border border-text-grey dark:border-brand-primary-200-light lg:scale-[1.2]'
			>
				{activeElement === 'consumption' && <IonIcon icon={logoEuro} />}
				{activeElement === 'production' && <IonIcon icon={leaf} />}
				{activeElement === 'nature' && <IonIcon icon={flash} />}
				{activeElement === 'money' ? (
					user?.panelLinked === true && inverter ? (
						<SolarPower sx={{ fontSize: '1.15em' }} />
					) : (
						<IonIcon icon={leaf} />
					)
				) : null}
			</div>

			<div key={activeElement} ref={caugeElement} className='cauge-element' />

			<div onClick={() => history.push('/exchange/consumption/day')}>
				<Slider ref={sliderRef} {...settings} className='cursor-pointer'>
					<MoneyCircle consideredMonth={consideredMonth} />
					<ConsumptionCircle consideredMonth={consideredMonth} comparisonMonth={comparisonMonth} />
					<NatureCircle consideredMonth={consideredMonth} />
					{user?.panelLinked === true && inverter && (
						<ProductionCircle inverter={inverter} month={consideredMonth.localTime.month} />
					)}
				</Slider>
			</div>
			<div
				onClick={() => {
					logEvent('click-circle-carousel-next')
					sliderRef.current.slickNext()
				}}
				className='small-circle button-hover text-text-grey-200 dark:text-background-200-light bg-white dark:bg-background-200-dark border-solid border border-text-grey dark:border-brand-primary-200-light lg:scale-[1.2]'
			>
				{activeElement === 'consumption' && <IonIcon icon={leaf} />}
				{activeElement === 'money' && <IonIcon icon={flash} />}
				{activeElement === 'production' && <IonIcon icon={logoEuro} />}
				{activeElement === 'nature' ? (
					user?.panelLinked === true && inverter ? (
						<SolarPower sx={{ fontSize: '1.15em' }} />
					) : (
						<IonIcon icon={logoEuro} />
					)
				) : null}
			</div>
			<div className='hidden lg:block' />
		</div>
	)
}

export default CircleCarousel
